/**
 * The density stress world (P2.0-T4, ADR-080's amendment of 2026-10-03): where each density tier stands on its one
 * map, and the route the page's benchmark mode takes through them. The generator in `tools/dev-server` places the
 * props and creature spawns from this table, the page's benchmark (`?bench=1`) follows the same table, and the
 * measurement tool reads it, so the three can never disagree about where a tier is.
 *
 * Everything is in tiles of map `main` (512 by 864 tiles, 16 by 27 chunks of 32). A prop tier is a block of 5 by 3
 * chunks, its props spread evenly over it, with two empty chunks between blocks, so while the camera pans along a
 * block's middle chunk row the loaded area (the view plus one chunk of margin, ADR-019) holds that tier's props and no
 * other's. A tier's `props` is its count per 9 chunks, so the block holds `props * 15 / 9`; a 1920 by 1080 page at
 * the default zoom loads 4 by 3 chunks, so it draws about 1.33 times `props`. The creature tiers stand in a row along
 * the bottom, 64 tiles apart, so a character among one sees none of the others (vision 27 by 15 tiles plus a margin,
 * ADR-034).
 */

/**
 * The stress world's id and its map.
 */
export const STRESS_WORLD = { worldId: "stress", worldName: "Density stress", mapId: "main", mapName: "Density stress", width: 512, height: 864 } as const;

/**
 * Tiles in a chunk side.
 */
export const STRESS_CHUNK = 32;

/**
 * A run of tiles, `[x0, x1)` by `[y0, y1)`.
 */
export interface StressRect {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * A point in tiles.
 */
export interface StressPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * What a prop tier holds: trees, bushes and rocks, or buildings with their fences, lamps, crates and gardens.
 */
export type StressKind = "forest" | "city";

/**
 * One prop tier: its id, kind, the props and the animated props it holds per 9 chunks, its block, and the pan the
 * benchmark takes along the block's middle chunk row, crossing two chunk boundaries.
 */
export interface PropTier {
  readonly id: string;
  readonly kind: StressKind;
  readonly props: number;
  readonly animated: number;
  readonly block: StressRect;
  readonly pan: { readonly from: StressPoint; readonly to: StressPoint };
}

/**
 * One creature tier: its id, how many creatures it keeps, the area they wander in and its centre.
 */
export interface CreatureTier {
  readonly id: string;
  readonly creatures: number;
  readonly area: StressRect;
  readonly centre: StressPoint;
}

/**
 * The levels every prop kind is built at, as props per 9 chunks.
 */
export const PROP_LEVELS: readonly number[] = [500, 2000, 5000, 10000];

/**
 * The creature counts of the crowd tiers.
 */
export const CREATURE_LEVELS: readonly number[] = [20, 50, 100, 200];

/**
 * The share of a tier's props that loop an animation, in percent.
 */
export const ANIMATED_PERCENT = 5;

/**
 * Chunks in a prop block's side, and between blocks.
 */
const BLOCK_CHUNKS = { width: 5, height: 3 } as const;
const GAP_CHUNKS = 2;

/**
 * The block of a prop tier by its kind's column and its level's row.
 *
 * @param column 0 for forest, 1 for city.
 * @param row The level's index.
 * @returns The block.
 */
function blockAt(column: number, row: number): StressRect {
  const x0 = (GAP_CHUNKS + column * (BLOCK_CHUNKS.width + GAP_CHUNKS)) * STRESS_CHUNK;
  const y0 = (GAP_CHUNKS + row * (BLOCK_CHUNKS.height + GAP_CHUNKS)) * STRESS_CHUNK;
  return { x0, y0, x1: x0 + BLOCK_CHUNKS.width * STRESS_CHUNK, y1: y0 + BLOCK_CHUNKS.height * STRESS_CHUNK };
}

/**
 * The prop tiers, forest then city, each from the lightest level to the heaviest.
 */
export const PROP_TIERS: readonly PropTier[] = (["forest", "city"] as const).flatMap((kind, column) =>
  PROP_LEVELS.map((props, row): PropTier => {
    const block = blockAt(column, row);
    const middle = block.y0 + STRESS_CHUNK * 1.5;
    return {
      id: `${kind}-${props}`,
      kind,
      props,
      animated: Math.round((props * ANIMATED_PERCENT) / 100),
      block,
      pan: { from: { x: block.x0 + 40, y: middle }, to: { x: block.x0 + 120, y: middle } },
    };
  }),
);

/**
 * The row the creature tiers and the spawn stand on: the middle of the bottom band of chunks.
 */
const CREATURE_ROW = (GAP_CHUNKS + PROP_LEVELS.length * (BLOCK_CHUNKS.height + GAP_CHUNKS)) * STRESS_CHUNK + STRESS_CHUNK * 1.5;

/**
 * Where a fresh character arrives: the west end of the creature row, out of sight of every crowd.
 */
export const STRESS_SPAWN: StressPoint = { x: 40, y: CREATURE_ROW };

/**
 * A crowd's area: 24 by 14 tiles about its centre.
 */
const CROWD = { halfWidth: 12, halfHeight: 7 } as const;

/**
 * The creature tiers, west to east, 64 tiles apart from 128 tiles in.
 */
export const CREATURE_TIERS: readonly CreatureTier[] = CREATURE_LEVELS.map((creatures, index): CreatureTier => {
  const centre = { x: 128 + index * 64, y: CREATURE_ROW };
  return { id: `crowd-${creatures}`, creatures, centre, area: { x0: centre.x - CROWD.halfWidth, y0: centre.y - CROWD.halfHeight, x1: centre.x + CROWD.halfWidth, y1: centre.y + CROWD.halfHeight } };
});

/**
 * Where the benchmark's walk through the crowds ends: 40 tiles east of the last crowd's centre.
 */
export const STRESS_WALK_END = (CREATURE_TIERS[CREATURE_TIERS.length - 1]?.centre.x ?? 0) + 40;

/**
 * Half the stretch of the walk a crowd is measured over, in tiles: ten seconds of walking at 3.75 tiles a second,
 * centred on the crowd.
 */
const CROWD_HALF_SPAN = 18.75;

/**
 * The stress world's benchmark route as plain data in the page benchmark's route format (format 1,
 * `odyssey-client/src/game/bench/route.ts`), which `routes/stress.json` holds: a 10 second hold on the character at
 * the spawn, then each prop tier as a pan along its block's middle chunk row after a 2 second rest at its start, then
 * a walk east along the crowd row, each crowd measured over the 10 seconds of the walk centred on it.
 *
 * @returns The route.
 */
export function stressRoute(): { readonly format: 1; readonly id: string; readonly world: string; readonly map: string; readonly steps: readonly Record<string, unknown>[] } {
  return {
    format: 1,
    id: "stress-v1",
    world: STRESS_WORLD.worldId,
    map: STRESS_WORLD.mapId,
    steps: [
      { kind: "hold", id: "baseline", note: "the character at the spawn, nothing near", measureMs: 10_000 },
      ...PROP_TIERS.map((tier) => ({ kind: "pan", id: tier.id, note: `${tier.props} props per 9 chunks (about ${Math.round(tier.props * 4 / 3)} drawn at 1920 by 1080), ${tier.animated} animated`, waypoints: [tier.pan.from, tier.pan.to], settleMs: 2_000, measureMs: 10_000 })),
      {
        kind: "walk",
        id: "crowds",
        direction: "right",
        until: STRESS_WALK_END,
        stuckMs: 3_000,
        windows: CREATURE_TIERS.map((tier) => ({ id: tier.id, note: `${tier.creatures} creatures`, from: tier.centre.x - CROWD_HALF_SPAN, to: tier.centre.x + CROWD_HALF_SPAN })),
      },
    ],
  };
}
